import { Field, Form, Formik, FormikConfig } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { Box } from '@mui/material';

import { TextField } from 'common/components/FormField';
import { DefaultButton } from 'common/components/Button/DefaultButton';
import { Button } from 'common/components/Button';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';

export type TFormValues = {
    title: string;
    description?: string;
};

interface IProps extends FormikConfig<TFormValues> {
    className?: string;
    disabled?: boolean;
    isDeleting?: boolean;
    onClose?(): void;
    onDelete?(): void;
}

const validationSchema = Yup.object().shape({
    title: Yup.string().required(),
    description: Yup.string().optional(),
});

const BaseDevelopmentItemForm = ({
    className,
    disabled,
    isDeleting,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    return (
        <Formik validationSchema={validationSchema} {...other}>
            {({ submitForm, isSubmitting, dirty }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting && !isDeleting}
                        onConfirm={submitForm}
                    />

                    <Form className={className}>
                        <Field
                            component={TextField}
                            label={translate('developmentItem')}
                            name="title"
                        />

                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    disabled ||
                                    (!dirty && !isSubmitting && !disabled)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    submitForm();
                                }}
                            >
                                {translate('save')}
                            </Button>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {!!onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};

export const DevelopmentItemForm = styled(BaseDevelopmentItemForm)`
    ${DefaultButton} {
        margin-top: ${({ theme }) => theme.spacing(2)};
    }
`;
